import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// ホワイトリスト設定の型
export interface AuthWhitelistSettings {
  // 許可するドメイン（Googleログイン用）
  allowedGoogleDomains: string[];
  // 個別に許可するメールアドレス
  allowedEmails: string[];
  // ドメイン制限を有効にするか（falseの場合は全員許可）
  enabled: boolean;
}

/**
 * Google ログインを許可するドメイン。
 *
 * 実運用の値は Firestore の settings/auth_whitelist が優先される。ここはその
 * フォールバックであり、ログイン画面の案内文の表示元でもある。
 *
 * ⚠️ Firestore 側の allowedGoogleDomains を変更したら、ここも必ず合わせること。
 * 食い違うと (1) Firestore の読み取りに失敗したときに許可されるべき人が弾かれ、
 * (2) ログイン画面の案内が実態と違う、という2つの問題が起きる。
 * ログイン画面は未認証のため Firestore を読めず（ルールで拒否される）、
 * ライブの値を出せないので、この定数を表示している。
 */
export const ALLOWED_GOOGLE_DOMAINS = [
  '@tv-asahi.co.jp',
  '@tv-asahipro.co.jp',
  '@ex5ch.com',
] as const;

// デフォルト設定（Firestoreにドキュメントがない場合）
const DEFAULT_SETTINGS: AuthWhitelistSettings = {
  allowedGoogleDomains: [...ALLOWED_GOOGLE_DOMAINS],
  allowedEmails: [],
  enabled: true,
};

/**
 * Firestoreからホワイトリスト設定を取得
 */
export const getAuthWhitelistSettings = async (): Promise<AuthWhitelistSettings> => {
  try {
    const docRef = doc(db, 'settings', 'auth_whitelist');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        allowedGoogleDomains: data.allowedGoogleDomains || DEFAULT_SETTINGS.allowedGoogleDomains,
        allowedEmails: data.allowedEmails || DEFAULT_SETTINGS.allowedEmails,
        enabled: data.enabled ?? DEFAULT_SETTINGS.enabled,
      };
    }

    // ドキュメントが存在しない場合はデフォルト設定を返す
    return DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error fetching auth whitelist settings:', error);
    // エラー時はデフォルト設定を返す（制限を有効にしたまま）
    return DEFAULT_SETTINGS;
  }
};

/**
 * メールアドレスがホワイトリストに含まれるかチェック
 * @param email ユーザーのメールアドレス
 * @param provider 認証プロバイダー（google/microsoft）
 * @returns 許可されているかどうか
 */
export const isEmailAllowed = async (
  email: string | null,
  provider: 'google' | 'microsoft',
): Promise<{ allowed: boolean; reason?: string }> => {
  if (!email) {
    return { allowed: false, reason: 'メールアドレスが取得できませんでした' };
  }

  // Microsoftログインは常に許可（Azure AD側で制限されているため）
  if (provider === 'microsoft') {
    return { allowed: true };
  }

  // Googleログインの場合はドメインチェック
  const settings = await getAuthWhitelistSettings();

  // 制限が無効の場合は全員許可
  if (!settings.enabled) {
    return { allowed: true };
  }

  // 個別メールアドレスのチェック
  const emailLower = email.toLowerCase();
  if (settings.allowedEmails.some((e) => e.toLowerCase() === emailLower)) {
    return { allowed: true };
  }

  // ドメインのチェック
  for (const domain of settings.allowedGoogleDomains) {
    if (emailLower.endsWith(domain.toLowerCase())) {
      return { allowed: true };
    }
  }

  return {
    allowed: false,
    reason: `このメールアドレス（${email}）でのGoogleログインは許可されていません。\n許可されているドメイン: ${settings.allowedGoogleDomains.join(', ')}`,
  };
};

/**
 * ユーザーが既存ユーザーかどうかをチェック
 * @param uid ユーザーID
 * @returns 既存ユーザーかどうか
 */
export const isExistingUser = async (uid: string): Promise<boolean> => {
  try {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    console.error('Error checking existing user:', error);
    return false;
  }
};
